import React, {useEffect, useState} from 'react';
import {
  FieldErrors,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import {Box, Heading} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {StackParamList} from '../../../../navigators';
import InternalTemplate from '../../../../components/templates/internal';
import {SpinnerModal} from '../../../../components/modals';
import {PrimaryButton, SecondaryButton} from '../../../../components/buttons';
import useToast from '../../../../hooks';
import {StyleSheet} from 'react-native';
import {DropDownInput} from '../../../../components/inputs';
import {IListOption} from '../../../../interfaces/globals';
import IDropDownOption from '../../../../interfaces/ui/dropdown-option.interface';
import {message} from '../../../../utils/messages';
import {IProduct} from '../../../../context/commons/interfaces';
import {usePortfolioFilterHook} from './hooks/portfolio-filter.hook';

type ScreenProps = NativeStackScreenProps<StackParamList, 'PortfolioFilter'>;

const FilterPortfolioScreen = ({navigation}: ScreenProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const {showInputsError, showServerError} = useToast();

  const {
    categoryList,
    cityList,
    productList,
    territoryList,
    fetchProducts,
    setFilter,
    filter,
    getGoBackScreen,
  } = usePortfolioFilterHook();

  const loadData = async () => {
    try {
      const result = await fetchProducts();
      if (!result) {
        showServerError();
      }
      setIsLoading(false);
    } catch (ex) {
      showServerError();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (productList == null) {
      loadData();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productList]);

  const {control, handleSubmit, reset, watch, setValue} = useForm({
    defaultValues: {
      productId: '',
      categoryId: '',
      subCategoryId: '',
      cityId: '',
      zoneId: '',
    } as any,
  });

  useEffect(() => {
    for (const property in filter) {
      setValue(property, (filter as any)[property]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const categorySelectedId = watch('categoryId');
  const productSelectedId = watch('productId');
  const subCategorySelectedId = watch('subCategoryId');
  const citySelectedId = watch('cityId');
  const zoneSelectedId = watch('zoneId');

  const [subCategoryList, setSubCategoryList] = useState([] as IListOption[]);

  useEffect(() => {
    if (categorySelectedId) {
      const categorySelected = categoryList.find(
        category => category.value === categorySelectedId,
      );
      if (categorySelected) {
        setSubCategoryList(categorySelected?.subCategories);
        return;
      }
    }
    setSubCategoryList([]);
  }, [categorySelectedId, categoryList]);

  const handleClearForm = () => {
    reset({
      productId: '',
      categoryId: '',
      subCategoryId: '',
      cityId: '',
      zoneId: '',
    });
  };

  const onInvalid: SubmitErrorHandler<any> = async (
    fieldErrors: FieldErrors<any>,
  ) => {
    if (fieldErrors) {
      showInputsError();
    }
  };

  const onSubmit: SubmitHandler<any> = async (data: any) => {
    let filterObj: any = null;

    for (const property in data) {
      if (data[property] !== '') {
        if (filterObj === null) {
          filterObj = {};
        }
        filterObj[property] = data[property];
      }
    }

    setFilter(filterObj);
    goBack();
  };

  const goBack = () => {
    navigation.navigate(getGoBackScreen());
  };

  const parseIListOption = (list: IListOption[]): IDropDownOption[] => {
    return list.map(item => {
      return {
        label: item.title,
        value: item.value,
      };
    });
  };

  const parseIProductOption = (list: IProduct[] | null): IDropDownOption[] => {
    if (list) {
      return list.map(item => {
        return {
          label: item.title,
          value: item.id,
        };
      });
    }
    return [];
  };

  const showClearButton = () => {
    return !(
      categorySelectedId === '' &&
      productSelectedId === '' &&
      subCategorySelectedId === '' &&
      citySelectedId === '' &&
      zoneSelectedId === ''
    );
  };

  return (
    <InternalTemplate
      title={'screen-filter-screen-title'}
      contentPaddingX="3"
      childContainerStyle={styles.childContainerStyle}
      backButton={{
        onPress: () => goBack(),
        label: 'back-resume',
        usage: 'external',
      }}>
      <SpinnerModal show={isLoading} />

      <Box marginBottom={20}>
        <Heading size={'sm'} color={'#06355E'} marginBottom={5} marginTop={2}>
          {message('salesman-portfolio-filter-heading')}
        </Heading>
        <>
          <DropDownInput
            control={control}
            name="productId"
            label="salesman-portfolio-filter-product-list"
            options={parseIProductOption(productList)}
          />
          <DropDownInput
            control={control}
            name="categoryId"
            label="salesman-portfolio-filter-category-list"
            options={parseIListOption(categoryList)}
          />

          <DropDownInput
            control={control}
            name="subcategoryId"
            label="salesman-portfolio-filter-subcategory-list"
            options={parseIListOption(subCategoryList)}
          />

          <DropDownInput
            control={control}
            name="cityId"
            label="salesman-portfolio-filter-city-list"
            options={parseIListOption(cityList)}
          />

          <DropDownInput
            control={control}
            name="zoneId"
            label="salesman-portfolio-filter-zona-list"
            options={parseIListOption(territoryList)}
          />

          <Box mt={'5'}>
            <PrimaryButton
              label="salesman-portfolio-filter-save-button"
              onPress={handleSubmit(onSubmit, onInvalid)}
            />
          </Box>
          {showClearButton() && (
            <Box mt={5}>
              <SecondaryButton
                label="salesman-portfolio-filter-clear-button"
                onPress={handleClearForm}
              />
            </Box>
          )}
        </>
      </Box>
    </InternalTemplate>
  );
};
export default FilterPortfolioScreen;

const styles = StyleSheet.create({
  childContainerStyle: {
    paddingBottom: '100%',
  },
});
